<template>
  <div>
    <validation-observer ref="refFormObserver">
      <b-form>
        <!-- Sección de API -->
        <h5 class="mb-2">{{ $t("proxy_rtsp_server.api_settings") }}</h5>
        <b-row>
          <b-col cols="12" lg="6">
            <form-field-toggle
              id="api"
              v-model="itemData.config.api"
              :label="$t('proxy_rtsp_server.enable_api')"
            />
          </b-col>
          <b-col cols="12" lg="6">
            <form-field-text
              id="apiAddress"
              v-model="itemData.config.apiAddress"
              :label="$t('proxy_rtsp_server.api_address')"
            />
          </b-col>
          <b-col cols="12" lg="6">
            <form-field-toggle
              id="apiEncryption"
              v-model="itemData.config.apiEncryption"
              :label="$t('proxy_rtsp_server.enable_api_encryption')"
            />
          </b-col>
          <b-col cols="12" lg="6">
            <form-field-text
              id="apiServerKey"
              v-model="itemData.config.apiServerKey"
              :label="$t('proxy_rtsp_server.api_server_key')"
            />
          </b-col>
          <b-col cols="12" lg="6">
            <form-field-text
              id="apiServerCert"
              v-model="itemData.config.apiServerCert"
              :label="$t('proxy_rtsp_server.api_server_cert')"
            />
          </b-col>
          <b-col cols="12" lg="6">
            <form-field-text
              id="apiAllowOrigin"
              v-model="itemData.config.apiAllowOrigin"
              :label="$t('proxy_rtsp_server.api_allow_origin')"
            />
          </b-col>
          <b-col cols="12" lg="6">
            <form-field-select
              id="apiTrustedProxies"
              v-model="itemData.config.apiTrustedProxies"
              :label="$t('proxy_rtsp_server.api_trusted_proxies')"
              :options="[]"
              multiple
              taggable
              :push-tags="true"
              :reduce-option="(option) => option"
            />
          </b-col>
        </b-row>

        <!-- Selector de método de autenticación -->
        <h5 class="mt-2 mb-2">{{ $t("proxy_rtsp_server.authentication") }}</h5>
        <b-row>
          <b-col cols="12" lg="6">
            <form-field-select
              id="authMethod"
              v-model="itemData.config.authMethod"
              :label="$t('proxy_rtsp_server.auth_method')"
              :options="authMethodOptions"
            />
          </b-col>
        </b-row>

        <!-- Sección de Autenticación Interna (visible cuando authMethod es "internal") -->
        <div v-if="itemData.config.authMethod === 'internal'" class="mt-1 mb-2">
          <div class="d-flex justify-content-between mb-2">
            <h5 class="mb-0">{{ $t("proxy_rtsp_server.auth_internal_users") }}</h5>
            <b-button variant="primary" size="sm" @click="addNewUser">
              <feather-icon icon="PlusIcon" class="mr-50" />
              {{ $t("add_user") }}
            </b-button>
          </div>
          <b-table
            :items="itemData.config.authInternalUsers"
            :fields="userTableFields"
            responsive
            striped
            hover
          >
            <template #cell(user)="data">
              {{ data.item.user }}
            </template>

            <template #cell(ips)="data">
              {{ data.item.ips.join(", ") || $t("any_ip") }}
            </template>

            <template #cell(permissions)="data">
              <b-badge
                v-for="perm in data.item.permissions"
                :key="`${data.index}-${perm.action}`"
                variant="light-primary"
                class="mr-1"
              >
                {{ perm.action }}{{ perm.path ? `: ${perm.path}` : "" }}
              </b-badge>
            </template>

            <template #cell(actions)="data">
              <b-button-group size="sm">
                <b-button variant="outline-primary" @click="editUser(data.index)">
                  <feather-icon icon="EditIcon" size="16" />
                </b-button>
                <b-button variant="outline-danger" @click="deleteUser(data.index)">
                  <feather-icon icon="TrashIcon" size="16" />
                </b-button>
              </b-button-group>
            </template>
          </b-table>
        </div>

        <!-- Sección de Autenticación HTTP (visible cuando authMethod es "http") -->
        <div v-if="itemData.config.authMethod === 'http'" class="mt-1 mb-2">
          <b-row class="d-flex justify-content-between mb-1">
            <b-col cols="12" lg="6">
              <form-field-text
                id="authHTTPAddress"
                v-model="itemData.config.authHTTPAddress"
                :label="$t('proxy_rtsp_server.auth_http_address')"
              />
            </b-col>
          </b-row>

          <div class="d-flex justify-content-between my-2">
            <h5 class="mb-0">{{ $t("proxy_rtsp_server.auth_http_exclude") }}</h5>
            <b-button variant="primary" size="sm" @click="addHttpExclude">
              <feather-icon icon="PlusIcon" class="mr-50" />
              {{ $t("add_exclusion") }}
            </b-button>
          </div>
          <b-table
            :items="itemData.config.authHTTPExclude"
            :fields="httpExcludeFields"
            responsive
            striped
            hover
            justify
          >
            <template #cell(action)="data">
              {{ data.item.action }}
            </template>

            <template #cell(path)="data">
              {{ data.item.path || $t("any_path") }}
            </template>

            <template #cell(actions)="data">
              <b-button-group size="sm">
                <b-button variant="outline-primary" @click="editHttpExclude(data.index)">
                  <feather-icon icon="EditIcon" size="16" />
                </b-button>
                <b-button variant="outline-danger" @click="deleteHttpExclude(data.index)">
                  <feather-icon icon="TrashIcon" size="16" />
                </b-button>
              </b-button-group>
            </template>
          </b-table>
        </div>

        <!-- Sección de Autenticación JWT (visible cuando authMethod es "jwt") -->
        <div v-if="itemData.config.authMethod === 'jwt'" class="mt-1 mb-3">
          <b-row>
            <b-col cols="12" lg="6">
              <form-field-text
                id="authJWTJWKS"
                v-model="itemData.config.authJWTJWKS"
                :label="$t('proxy_rtsp_server.auth_jwt_jwks')"
              />
            </b-col>
            <b-col cols="12" lg="6">
              <form-field-text
                id="authJWTJWKSFingerprint"
                v-model="itemData.config.authJWTJWKSFingerprint"
                :label="$t('proxy_rtsp_server.auth_jwt_jwks_fingerprint')"
              />
            </b-col>
            <b-col cols="12" lg="6">
              <form-field-text
                id="authJWTClaimKey"
                v-model="itemData.config.authJWTClaimKey"
                :label="$t('proxy_rtsp_server.auth_jwt_claim_key')"
              />
            </b-col>
            <b-col cols="12" lg="6">
              <form-field-toggle
                id="authJWTInHTTPQuery"
                v-model="itemData.config.authJWTInHTTPQuery"
                :label="$t('proxy_rtsp_server.auth_jwt_in_http_query')"
              />
            </b-col>
          </b-row>

          <div class="mt-2 mb-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h5 class="mb-0">{{ $t("proxy_rtsp_server.auth_jwt_exclude") }}</h5>
              <b-button variant="primary" size="sm" @click="addJwtExclude">
                <feather-icon icon="PlusIcon" class="mr-50" />
                {{ $t("add_exclusion") }}
              </b-button>
            </div>
            <b-table
              :items="itemData.config.authJWTExclude"
              :fields="jwtExcludeFields"
              responsive
              striped
              hover
            >
              <template #cell(action)="data">
                {{ data.item.action }}
              </template>

              <template #cell(path)="data">
                {{ data.item.path || $t("any_path") }}
              </template>

              <template #cell(actions)="data">
                <b-button-group size="sm">
                  <b-button variant="outline-primary" @click="editJwtExclude(data.index)">
                    <feather-icon icon="EditIcon" size="16" />
                  </b-button>
                  <b-button
                    variant="outline-danger"
                    @click="deleteJwtExclude(data.index)"
                  >
                    <feather-icon icon="TrashIcon" size="16" />
                  </b-button>
                </b-button-group>
              </template>
            </b-table>
          </div>
        </div>
      </b-form>
    </validation-observer>

    <!-- Modal para usuarios internos -->
    <b-modal
      v-model="isUserModalOpen"
      :header-bg-variant="headerBgVariant"
      :header-text-variant="headerTextVariant"
      :title="isEditingUser ? $t('edit_user') : $t('add_user')"
      @hidden="resetUserForm"
      @ok="saveUser"
      ok-title="Save"
      cancel-title="Cancel"
      no-close-on-backdrop
      no-close-on-esc
    >
      <b-form>
        <b-form-group :label="$t('username')" label-for="user-name">
          <b-form-input id="user-name" v-model="userForm.user" required />
        </b-form-group>

        <b-form-group :label="$t('password')" label-for="user-pass">
          <b-form-input
            id="user-pass"
            v-model="userForm.pass"
            type="password"
            :disabled="userForm.user === 'any'"
            :placeholder="
              userForm.user === 'any' ? $t('proxy_rtsp_server.password_not_required_for_user_any') : ''
            "
          />
        </b-form-group>

        <b-form-group
          :label="$t('allowed_ips')"
          label-for="user-ips"
          :description="$t('allowed_ips_description')"
        >
          <b-form-input
            id="user-ips"
            v-model="userIpsInput"
            placeholder="127.0.0.1/32, 0.0.0.0/24"
          />
        </b-form-group>

        <b-form-group :label="$t('permissions')">
          <div
            v-for="(perm, index) in userForm.permissions"
            :key="index"
            class="d-flex align-items-center mb-1"
          >
            <b-form-select
              v-model="perm.action"
              :options="permissionOptions"
              class="mr-1"
            />

            <b-form-input
              v-model="perm.path"
              placeholder="Path (optional)"
              class="mr-1"
            />

            <b-button variant="outline-danger" size="sm" @click="removePermission(index)">
              <feather-icon icon="XIcon" size="14" />
            </b-button>
          </div>

          <b-button
            variant="outline-primary"
            size="sm"
            class="mt-1"
            @click="addPermission"
          >
            <feather-icon icon="PlusIcon" size="14" class="mr-50" />
            {{ $t("add_permission") }}
          </b-button>
        </b-form-group>
      </b-form>
    </b-modal>

    <!-- Modal para exclusiones HTTP -->
    <b-modal
      v-model="isHttpExcludeModalOpen"
      :header-bg-variant="headerBgVariant"
      :header-text-variant="headerTextVariant"
      :title="isEditingHttpExclude ? $t('edit_exclusion') : $t('add_exclusion')"
      @hidden="resetHttpExcludeForm"
      @ok="saveHttpExclude"
      ok-title="Save"
      cancel-title="Cancel"
      no-close-on-backdrop
      no-close-on-esc
    >
      <b-form>
        <b-form-group :label="$t('action')" label-for="http-exclude-action">
          <b-form-select
            id="http-exclude-action"
            v-model="httpExcludeForm.action"
            :options="permissionOptions"
            required
          />
        </b-form-group>

        <b-form-group :label="$t('path')" label-for="http-exclude-path">
          <b-form-input
            id="http-exclude-path"
            v-model="httpExcludeForm.path"
            placeholder="Path (optional)"
          />
        </b-form-group>
      </b-form>
    </b-modal>

    <!-- Modal para exclusiones JWT -->
    <b-modal
      v-model="isJwtExcludeModalOpen"
      :header-bg-variant="headerBgVariant"
      :header-text-variant="headerTextVariant"
      :title="isEditingJwtExclude ? $t('edit_exclusion') : $t('add_exclusion')"
      @hidden="resetJwtExcludeForm"
      @ok="saveJwtExclude"
      ok-title="Save"
      cancel-title="Cancel"
      no-close-on-backdrop
      no-close-on-esc
    >
      <b-form>
        <b-form-group :label="$t('action')" label-for="jwt-exclude-action">
          <b-form-select
            id="jwt-exclude-action"
            v-model="jwtExcludeForm.action"
            :options="permissionOptions"
            required
          />
        </b-form-group>

        <b-form-group :label="$t('path')" label-for="jwt-exclude-path">
          <b-form-input
            id="jwt-exclude-path"
            v-model="jwtExcludeForm.path"
            placeholder="Path (optional)"
          />
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import FormFieldText from "@/components/WizardModal/FormFields/FormFieldText.vue";
import FormFieldToggle from "@/components/WizardModal/FormFields/FormFieldToggle.vue";
import FormFieldSelect from "@/components/WizardModal/FormFields/FormFieldSelect.vue";
import { ValidationObserver } from "vee-validate";
import useRtpsServerWizard from "./useRtpsServerWizard";
import { ref } from "@vue/composition-api";

export default {
  name: "ApiStep",
  components: {
    FormFieldText,
    FormFieldToggle,
    FormFieldSelect,
    ValidationObserver,
  },
  props: {
    propItemData: {
      type: Object,
      required: true,
    },
  },
  setup(props, context) {
    const configDefaults = {
      api: false,
      apiAddress: "",
      apiEncryption: false,
      apiServerKey: "",
      apiServerCert: "",
      apiAllowOrigin: "",
      apiTrustedProxies: [],

      // Configuración de autenticación
      authMethod: "internal",

      // Configuración de autenticación interna
      authInternalUsers: [
        {
          user: "any",
          pass: "",
          ips: [],
          permissions: [
            { action: "publish", path: "" },
            { action: "read", path: "" },
            { action: "playback", path: "" },
          ],
        },
        {
          user: "any",
          pass: "",
          ips: ["127.0.0.1/32", "::1/128"],
          permissions: [
            { action: "api", path: "" },
            { action: "metrics", path: "" },
            { action: "pprof", path: "" },
          ],
        },
      ],

      // Configuración de autenticación HTTP
      authHTTPAddress: "",
      authHTTPExclude: [
        { action: "api", path: "" },
        { action: "metrics", path: "" },
        { action: "pprof", path: "" },
      ],

      // Configuración de autenticación JWT
      authJWTJWKS: "",
      authJWTJWKSFingerprint: "",
      authJWTClaimKey: "mediamtx_permissions",
      authJWTExclude: [],
      authJWTInHTTPQuery: true,
    };

    const authMethodOptions = ref([
      { value: "internal", text: "Internal" },
      { value: "http", text: "HTTP" },
      { value: "jwt", text: "JWT" },
    ]);

    const permissionOptions = ref([
      { value: "publish", text: "Publish" },
      { value: "read", text: "Read" },
      { value: "playback", text: "Playback" },
      { value: "api", text: "API" },
      { value: "metrics", text: "Metrics" },
      { value: "pprof", text: "Pprof" },
    ]);

    const { itemData, refFormObserver, getValidationState, validateStep } =
      useRtpsServerWizard(props, context, configDefaults);

    // ----- Gestión de usuarios internos -----
    const isUserModalOpen = ref(false);
    const isEditingUser = ref(false);
    const editingUserIndex = ref(-1);
    const userForm = ref({
      user: "",
      pass: "",
      ips: [],
      permissions: [],
    });
    const userIpsInput = ref("");
    const userTableFields = [
      { key: "user", label: "Username" },
      { key: "ips", label: "IP Restrictions" },
      { key: "permissions", label: "Permissions" },
      { key: "actions", label: "Actions" },
    ];

    const addNewUser = () => {
      isEditingUser.value = false;
      editingUserIndex.value = -1;
      userForm.value = {
        user: "",
        pass: "",
        ips: [],
        permissions: [
          { action: "publish", path: "" },
          { action: "read", path: "" },
        ],
      };
      userIpsInput.value = "";
      isUserModalOpen.value = true;
    };

    const editUser = (index) => {
      isEditingUser.value = true;
      editingUserIndex.value = index;
      const user = itemData.value.config.authInternalUsers[index];
      userForm.value = JSON.parse(JSON.stringify(user));
      userIpsInput.value = user.ips.join(", ");
      isUserModalOpen.value = true;
    };

    const deleteUser = (index) => {
      if (confirm("¿Está seguro de que desea eliminar este usuario?")) {
        const currentUsers = [...itemData.value.config.authInternalUsers];
        currentUsers.splice(index, 1);
        itemData.value.config.authInternalUsers = currentUsers;
      }
    };

    const saveUser = (event) => {
      event.preventDefault();

      if (!userForm.value.user) {
        alert("Username is required");
        return;
      }

      if (userForm.value.user === 'any') {
        userForm.value.pass = '';
      }

      userForm.value.ips = userIpsInput.value
        .split(",")
        .map((ip) => ip.trim())
        .filter((ip) => ip);

      const currentUsers = [...itemData.value.config.authInternalUsers];

      if (isEditingUser.value) {
        currentUsers[editingUserIndex.value] = JSON.parse(JSON.stringify(userForm.value));
      } else {
        currentUsers.push(JSON.parse(JSON.stringify(userForm.value)));
      }

      itemData.value.config.authInternalUsers = currentUsers;
      isUserModalOpen.value = false;
    };

    const resetUserForm = () => {
      userForm.value = {
        user: "",
        pass: "",
        ips: [],
        permissions: [],
      };
      userIpsInput.value = "";
    };

    const addPermission = () => {
      userForm.value.permissions.push({ action: "read", path: "" });
    };

    const removePermission = (index) => {
      userForm.value.permissions.splice(index, 1);
    };

    const isHttpExcludeModalOpen = ref(false);
    const isEditingHttpExclude = ref(false);
    const editingHttpExcludeIndex = ref(-1);
    const httpExcludeForm = ref({
      action: "",
      path: "",
    });
    const httpExcludeFields = [
      { key: "action", label: "Action" },
      { key: "path", label: "Path" },
      { key: "actions", label: "Actions" },
    ];

    const addHttpExclude = () => {
      isEditingHttpExclude.value = false;
      editingHttpExcludeIndex.value = -1;
      httpExcludeForm.value = {
        action: "api",
        path: "",
      };
      isHttpExcludeModalOpen.value = true;
    };

    const editHttpExclude = (index) => {
      isEditingHttpExclude.value = true;
      editingHttpExcludeIndex.value = index;
      const exclude = itemData.value.config.authHTTPExclude[index];
      httpExcludeForm.value = JSON.parse(JSON.stringify(exclude));
      isHttpExcludeModalOpen.value = true;
    };

    const deleteHttpExclude = (index) => {
      if (confirm("¿Está seguro de que desea eliminar esta exclusión?")) {
        const currentExcludes = [...itemData.value.config.authHTTPExclude];
        currentExcludes.splice(index, 1);
        itemData.value.config.authHTTPExclude = currentExcludes;
      }
    };

    const saveHttpExclude = (event) => {
      event.preventDefault();
      if (!httpExcludeForm.value.action) {
        alert("Action is required");
        return;
      }

      const currentExcludes = [...itemData.value.config.authHTTPExclude];

      if (isEditingHttpExclude.value) {
        currentExcludes[editingHttpExcludeIndex.value] = JSON.parse(
          JSON.stringify(httpExcludeForm.value)
        );
      } else {
        currentExcludes.push(JSON.parse(JSON.stringify(httpExcludeForm.value)));
      }

      itemData.value.config.authHTTPExclude = currentExcludes;

      isHttpExcludeModalOpen.value = false;
    };

    const resetHttpExcludeForm = () => {
      httpExcludeForm.value = {
        action: "",
        path: "",
      };
    };

    // ----- JWT -----
    const isJwtExcludeModalOpen = ref(false);
    const isEditingJwtExclude = ref(false);
    const editingJwtExcludeIndex = ref(-1);
    const jwtExcludeForm = ref({
      action: "",
      path: "",
    });
    const jwtExcludeFields = [
      { key: "action", label: "Action" },
      { key: "path", label: "Path" },
      { key: "actions", label: "Actions" },
    ];

    const addJwtExclude = () => {
      isEditingJwtExclude.value = false;
      editingJwtExcludeIndex.value = -1;
      jwtExcludeForm.value = {
        action: "api",
        path: "",
      };
      isJwtExcludeModalOpen.value = true;
    };

    const editJwtExclude = (index) => {
      isEditingJwtExclude.value = true;
      editingJwtExcludeIndex.value = index;
      const exclude = itemData.value.config.authJWTExclude[index];
      jwtExcludeForm.value = JSON.parse(JSON.stringify(exclude));
      isJwtExcludeModalOpen.value = true;
    };

    const deleteJwtExclude = (index) => {
      if (confirm("¿Está seguro de que desea eliminar esta exclusión?")) {
        const currentExcludes = [...itemData.value.config.authJWTExclude];
        currentExcludes.splice(index, 1);
        itemData.value.config.authJWTExclude = currentExcludes;
      }
    };

    const saveJwtExclude = (event) => {
      event.preventDefault();

      if (!jwtExcludeForm.value.action) {
        alert("Action is required");
        return;
      }

      const currentExcludes = [...itemData.value.config.authJWTExclude];

      if (isEditingJwtExclude.value) {
        currentExcludes[editingJwtExcludeIndex.value] = JSON.parse(
          JSON.stringify(jwtExcludeForm.value)
        );
      } else {
        currentExcludes.push(JSON.parse(JSON.stringify(jwtExcludeForm.value)));
      }

      itemData.value.config.authJWTExclude = currentExcludes;
      isJwtExcludeModalOpen.value = false;
    };

    const resetJwtExcludeForm = () => {
      jwtExcludeForm.value = {
        action: "",
        path: "",
      };
    };

    return {
      itemData,
      refFormObserver,
      getValidationState,
      validateStep,
      authMethodOptions,
      permissionOptions,

      // Usuarios internos
      userTableFields,
      isUserModalOpen,
      isEditingUser,
      userForm,
      userIpsInput,
      addNewUser,
      editUser,
      deleteUser,
      saveUser,
      resetUserForm,
      addPermission,
      removePermission,

      // Exclusiones HTTP
      httpExcludeFields,
      isHttpExcludeModalOpen,
      isEditingHttpExclude,
      httpExcludeForm,
      addHttpExclude,
      editHttpExclude,
      deleteHttpExclude,
      saveHttpExclude,
      resetHttpExcludeForm,

      // Exclusiones JWT
      jwtExcludeFields,
      isJwtExcludeModalOpen,
      isEditingJwtExclude,
      jwtExcludeForm,
      addJwtExclude,
      editJwtExclude,
      deleteJwtExclude,
      saveJwtExclude,
      resetJwtExcludeForm,

      // Estilos
      headerBgVariant: "dark",
      headerTextVariant: "light",
    };
  },
};
</script>

<style scoped>
.permission-badge {
  margin-right: 0.25rem;
  margin-bottom: 0.25rem;
}

[dir] .modal .modal-header {
  background-color: var(--main-dark) !important;
}

.card-header {
  background-color: rgba(115, 103, 240, 0.1);
}
</style>
